import styled, { css } from 'styled-components';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import listStyled from 'components/List/styles';
import { animated } from 'react-spring';
import { DEEP_BORDER_BOX } from 'common';

const {
  SPACING,
  TYPOGRAPHY,
  RADIUS,
} = Tokens;

const TEXT_TRUNCATE_STYLE = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const TREE_BRANCH_FOCUSED = css`
  :focus {
    ${({ disabled }) => !disabled &&
      css`
      border: 0.125rem solid ${({ $theme }) => $theme && $theme.focused.border};
      padding: 0.125rem 0 0.125rem 0.375rem;
      ${({ RTL }) => !RTL && css`
        ${props => css`padding-left: ${props.level ? `${(props.level * 1) + 0.375}rem` : '0.375rem'};`}
      `};
      ${({ RTL }) => RTL && css`
        ${props => css`padding-right: ${props.level ? `${(props.level * 1) + 0.375}rem` : '0.375rem'};`}
      `};
    `};
  }
`;
const TREE_NODE_FOCUSED = css`
  :focus {
    ${({ disabled }) => !disabled &&
      css`
      border: 0.125rem solid ${({ $theme }) => $theme && $theme.focused.border};
      padding: 0.125rem 0 0.125rem 0.375rem;
      ${({ RTL }) => !RTL && css`
        ${props => css`padding-left: ${props.level ? `${(props.level * 1) + 2 + (props.level > 0 ? -0.125 : 0)}rem` : '0.375rem'};`}
      `};
      ${({ RTL }) => RTL && css`
        ${props => css`padding-right: ${props.level ? `${(props.level * 1) + 2 + (props.level > 0 ? -0.125 : 0)}rem` : '0.375rem'};`}
      `};
    `};
  }
`;
const TREE_ITEM_HOVERED = css`
  :hover {
    ${({ disabled }) => !disabled &&
      css`
      background-color: ${({ $theme }) => $theme && $theme.hovered.background};
      ${props => css`cursor: ${props.dnd ? 'grab' : 'default'};`}
      ${({ selected }) => selected && css`
        background-color: ${({ $theme }) => $theme && $theme.selected.hovered.background};
      `};
    `};
  }
`;
const getShadow = (shadowColorOne, shadowColorTwo, spreadingValue) => {
  let shadow = `0 0 ${spreadingValue} 0 ${shadowColorOne},`;
  shadow += ` 0 ${spreadingValue} ${spreadingValue} 0 ${shadowColorTwo}`;
  return shadow;
};
const TREE_ITEM_DRAG = css`
  ${({ isDragging }) => isDragging && css`
    background-color: ${({ $theme }) => $theme && $theme.drag.background};
    border: ${SPACING.SPACING_2} solid ${({ $theme }) => $theme && $theme.drag.border};
    padding: ${SPACING.SPACING_2} 0 ${SPACING.SPACING_2} 0.375rem;
    opacity: 0; // make item being dragged not visible in Tree
    box-shadow: ${getShadow(props => props.theme.TREE.drag.shadowColorOne, props => props.theme.TREE.drag.shadowColorOne, props => props.theme.TREE.drag.spreadingValue)};
  `};
`;
const TREE_BRANCH_DRAG_IS_OVER = css`
  ${({ disabled }) => !disabled &&
    css`
    border: ${SPACING.SPACING_2} solid ${({ $theme }) => $theme && $theme.focused.border};
    padding: ${SPACING.SPACING_2} 0 ${SPACING.SPACING_2} 0.375rem;
    ${({ RTL }) => !RTL && css`
      ${props => css`padding-left: ${props.level ? `${(props.level * 1) + 0.375}rem` : '0.375rem'};`}
    `};
    ${({ RTL }) => RTL && css`
      ${props => css`padding-right: ${props.level ? `${(props.level * 1) + 0.375}rem` : '0.375rem'};`}
    `};
  `};
`;
const TREE_ITEM_DISABLED = css`
  ${({ disabled }) => disabled &&
    css`
      color: ${({ $theme }) => $theme && $theme.disabled.text};
      background-color: ${({ $theme }) => $theme && $theme.disabled.background};
      pointer-events: none;
  `};
`;
const TREE_ICON_DISABLED = css`
  ${({ disabled }) => disabled && css`
    svg {
      & * {
        fill: ${({ $theme }) => $theme && $theme.disabled.icon};
        pointer-events: none;
      }
  `};
`;

const TREE_ITEM_TEXT = {
  ...TYPOGRAPHY.BODY,
  alignSelf: 'center',
};

const TREE_ITEM = css`
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  overflow: initial;
  outline: none;
  cursor: default;
  ${({ selected }) => selected && css`
    background-color: ${({ $theme }) => $theme && $theme.selected.background};
  `};
`;

const TreeContainer = styled.div`
  ${DEEP_BORDER_BOX}
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  border-radius: ${RADIUS.RADIUS_2};
  padding: 0;
  overflow: auto;
`;

const TreeWrapper = styled.div`
  height: 100%;
  width: 100%;
  max-width: 100%;
`;

const TreeBranch = styled.li`
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
  border: none;
  display: block;
  padding: 0;
  height: auto;
`;

const TreeBranchContent = styled.div`
  height: ${SPACING.SPACING_32};
  box-sizing: border-box;
  padding: ${SPACING.SPACING_4} ${SPACING.SPACING_2} ${SPACING.SPACING_4} ${SPACING.SPACING_8};
  ${({ RTL }) => !RTL && css`
    ${props => css`padding-left: ${props.level ? `${(props.level * 1) + 0.50}rem` : SPACING.SPACING_8};`}
  `};
  ${({ RTL }) => RTL && css`
    ${props => css`padding-right: ${props.level ? `${(props.level * 1) + 0.50}rem` : SPACING.SPACING_8};`}
  `};
  display: flex;
  flex-direction: row;
  align-items:center;
  justify-content: flex-start;
  outline: none;
  svg {
    pointer-events: none;
  }
  color: ${({ $theme }) => $theme && $theme.text};
  background-color: ${({ $theme }) => $theme && $theme.background};
  ${TREE_BRANCH_FOCUSED}
  &:focus-within {
    ${({ disabled }) => !disabled && css`
      border: ${SPACING.SPACING_2} solid ${({ $theme }) => $theme && $theme.focused.border};
      padding: ${SPACING.SPACING_2} 0 ${SPACING.SPACING_2} 0.375rem;
      ${({ RTL }) => !RTL && css`
        ${props => css`padding-left: ${props.level ? `${(props.level * 1) + 0.375}rem` : '0.375rem'};`}
      `};
      ${({ RTL }) => RTL && css`
        ${props => css`padding-right: ${props.level ? `${(props.level * 1) + 0.375}rem` : '0.375rem'};`}
      `};
  `};
  }
  ${({ selected }) => selected && css`
    background-color: ${({ $theme }) => $theme && $theme.selected.background};
  `};
  ${TREE_ITEM_HOVERED}
  ${TREE_ITEM_DISABLED}
  ${TREE_ITEM_DRAG}
  ${props => props.$isOver && TREE_BRANCH_DRAG_IS_OVER}
`;

const TreeContextMenu = styled.div`
  ${props => css`right: ${(props.RTL || props.$rtl || props.rtl) ? 'unset' : '0.5rem'};`};
  ${props => css`left: ${(props.RTL || props.$rtl || props.rtl) ? '0.5rem' : 'unset'};`};
  position: sticky;
  height: 2rem;
  svg {
    & * {
      fill: ${props => !props.retainColor && props.$theme && props.$theme.icon};
    }
  }
  ${({ hide }) => hide && css`
    opacity: 0;
  `};
  &:before {
    content: '';
    position: absolute;
    ${props => css`right: ${(props.RTL || props.$rtl || props.rtl) ? 'unset' : '0.5rem'};`};
    ${props => css`left: ${(props.RTL || props.$rtl || props.rtl) ? '0.5rem' : 'unset'};`};
    width: 1.875rem;
    height: 1.875rem;
  }
`;

const TreeBranchList = styled.ul`
  list-style-type: none;
  border: none;
  margin: 0;
  padding: 0;
  display: table;
  min-width: 100%;
  height: auto;
`;

const TreeNodeWrapper = styled.div`
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const TreeNode = styled.li`
  line-height: 1rem;
  height: 2rem;
  align-items: center;
  ${({ RTL }) => !RTL && css`
    ${props => css`padding-left: ${props.level ? `${(props.level * 1) + 2}rem` : '0.5rem'};`}
    padding-right: 0.125rem;
  `};
  ${({ RTL }) => RTL && css`
    ${props => css`padding-right: ${props.level ? `${(props.level * 1) + 2}rem` : '0.5rem'};`}
    padding-left: 0.125rem;
  `};
  ${TREE_NODE_FOCUSED}
  &:focus-within {
    ${({ allowEdit }) => !allowEdit && css`
      border: 0.125rem solid ${({ $theme }) => $theme && $theme.focused.border};
      padding-right: 0;
      ${({ RTL }) => !RTL && css`
        ${props => css`padding-left: ${props.level ? `${(props.level * 1) + 2 + (props.level > 0 ? -0.125 : 0)}rem` : '0.375rem'};`}
      `};
      ${({ RTL }) => RTL && css`
        ${props => css`padding-right: ${props.level ? `${(props.level * 1) + 2 + (props.level > 0 ? -0.125 : 0)}rem` : '0.375rem'};`}
      `};
    `};
  }
  color: ${({ $theme }) => $theme && $theme.text};
  background-color: ${({ $theme }) => $theme && $theme.background};
  ${TREE_ITEM}
  ${TREE_ITEM_DISABLED}
  ${TREE_ITEM_DRAG}
  ${TREE_ITEM_HOVERED}
`;

const TreeIcon = styled.div`
  ${props => css`margin: ${(props.RTL || props.$rtl || props.rtl) ? `0 0 0 ${SPACING.SPACING_8}` : `0 ${SPACING.SPACING_8} 0 0`};`}
  svg {
    & * {
      fill: ${props => !props.retainColor && props.$theme && props.$theme.icon};
    }
  }
  ${TREE_ICON_DISABLED}
  :focus {
    outline: none
  }
`;

const TreeText = styled.div`
    user-select: none;
    white-space: nowrap;
    width: 100%;
    ${TREE_ITEM_TEXT}
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    display: inherit;
    color: ${({ $theme }) => $theme && $theme.text}};
    ${({ $truncateListText }) => $truncateListText && css`
      ${TEXT_TRUNCATE_STYLE}
      display: block;
    `};
    ${({ disabled }) => disabled &&
    css`
      color: ${({ $theme }) => $theme && $theme.disabled.text};
  `};
    ${({ error }) => error && css`
      border: 0.125rem solid ${({ $theme }) => $theme && $theme.error.border}};
      padding-left: 0;
      padding-top: 0.125rem;
      padding-bottom: 0.125rem;
    `};
  `;

const TreeInputStyle = styled.div`
  box-shadow: none;
  box-sizing: border-box;
  width: 100%;
  input {
    box-shadow: none;
    height: 1.75rem;
    :focus {
      box-shadow: inset 0rem 0rem 0rem 0.125rem ${({ $theme }) => $theme && $theme.focused.border};
    }
  }
`;

const TreeDropDownButton = styled(animated.div)`
  button {
    min-width: 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
    &:hover:not(:disabled) {
      background-color: transparent;
      & svg * {
        fill: ${({ $theme }) => $theme && $theme.icon};
      }
    }
    &:focus:not(:disabled) {
      background-color: transparent;
      box-shadow: none;
      & svg * {
        fill: ${({ $theme }) => $theme && $theme.icon};
      }
    }
  }
  height: 1.5rem;
  width: 1.5rem;
`;

const TreeFooter = styled.div`
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
  background-color: transparent;
  display: flex;
  flex-direction: row;
  box-sizing: content-box;
  height: 4.375rem;
  padding-top: 1.625rem;
  box-shadow: 0 -0.0625rem 0.0625rem 0 ${({ $theme }) => $theme && $theme.divider};
  justify-content: flex-end;
`;

const TreeFilterContainer = styled.div`
  padding: 0.0625rem ${SPACING.SPACING_24} 0.0625rem ${SPACING.SPACING_24};
`;
const TreeFilterMatchesContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  box-sizing: border-box;
  height: 2.3125rem;
  color: ${({ $theme }) => $theme && $theme.text};
  ${TYPOGRAPHY.BODY}
  ${props => css`box-shadow: ${props.shadow ? `inset 0rem -0.0625rem 0rem 0rem  ${({ $theme }) => $theme && $theme.divider}}` : 'none'};`}
`;

const TreeFilterIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 0.3125rem;
  width: 2.125rem;
  ${props => props.$rtl && css`
    left: initial;
    right: .25rem;
  `};
  svg * {
    fill: ${({ $theme }) => $theme && $theme.icon};
  }
`;

const TreeFilterClearButton = styled.div`
  position: absolute;
  right: 0;
  top: 0.125rem;
  left: initial;
  ${props => props.$rtl && css`
    left: 0;
    right: initial;
  `};
`;

const TreeDragWrapper = styled.div`
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-left: 1rem;
`;
const TreeDragCircle = styled.div`
  border: 0.125rem solid ${({ $theme }) => $theme && $theme.drag.lineColor};
  width: 0.5rem;
  height: 0.5rem;
  border-radius: ${RADIUS.RADIUS_CIRCLE};
`;
const TreeDragLine = styled.div`
  background-color: ${({ $theme }) => $theme && $theme.drag.lineColor};
  width: calc(100% - 1.5rem);
  height: 0.125rem;
  align-self: center;
`;

const TreeMoreButtonItem = styled(listStyled.ListItemBasic)`
  &:hover {
    cursor: pointer;
  }
`;

const TreeLabelContent = styled.div`
  color: ${({ $theme }) => $theme && $theme.title};
  ${TYPOGRAPHY.LABEL}
`;

const TreeErrorContainer = styled.div`
  max-height: 2.125rem;
  width: 100%;
  max-width: 100%;
  margin-top: 0.25rem;
  ${({ RTL }) => !RTL && css`
    ${props => css`padding-left: ${props.level ? `${(props.level * 1) + 2}rem` : '0.5rem'};`}
    padding-right: 0.125rem;
  `};
  ${({ RTL }) => RTL && css`
    ${props => css`padding-right: ${props.level ? `${(props.level * 1) + 2}rem` : '0.5rem'};`}
    padding-left: 0.125rem;
  `};
`;

const TreeMoreButton = styled.div`
  button {
    height: 2rem;
    width: 2rem;
  }
`;

const TreeCheckbox = styled.div`
  margin-top: 0.1875rem;
  ${props => css`padding: ${(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_2} 0 0 ${SPACING.SPACING_8}` : `${SPACING.SPACING_2} ${SPACING.SPACING_4} 0 0`};`}
  :focus {
    border:  none;
    outline: none;
  }
`;

const styles = {
  TreeWrapper,
  TreeBranch,
  TreeBranchContent,
  TreeContextMenu,
  TreeBranchList,
  TreeIcon,
  TreeText,
  TreeContainer,
  TreeFooter,
  TreeInputStyle,
  TreeNode,
  TreeNodeWrapper,
  TreeDropDownButton,
  TreeFilterIcon,
  TreeFilterClearButton,
  TreeFilterContainer,
  TreeFilterMatchesContainer,
  TreeDragWrapper,
  TreeDragCircle,
  TreeDragLine,
  TreeMoreButtonItem,
  TreeLabelContent,
  TreeErrorContainer,
  TreeMoreButton,
  TreeCheckbox,
};

export default styles;